import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { ApiError, enderecoDeArquivo } from '@/shared/api/client';
import {
  policyholderCcg,
  type Policyholder,
  type PolicyholderCcgDetail,
  type PolicyholderCcgInput,
  type PolicyholderCcgParty,
  type PolicyholderCcgVersion,
} from '@/shared/api/resources';
import {
  formatCivilDate,
  formatDateTime,
  formatMoney,
  moneyInputError,
  moneyToInput,
  toMoney,
} from '@/shared/lib/format';
import { Alert } from '@/shared/ui/Alert';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { DataTable } from '@/shared/ui/DataTable';
import { Field } from '@/shared/ui/Field';
import { Modal } from '@/shared/ui/Modal';
import { MoneyField } from '@/shared/ui/MoneyField';
import { Textarea } from '@/shared/ui/Textarea';
import { useToast } from '@/shared/ui/useToast';

import { fieldErrorOf } from '../../shared/fieldError';
import { papeisDaCcg } from '../rotulos';
import { AcaoIndisponivel, Bloco, Info, InfoGrid } from './shared';

/**
 * Contrato de CCG do tomador (P-TOM-C3), dentro da secao CCG da ficha.
 *
 * As partes (representantes e fiadores) continuam no cadastro da secao. Aqui fica o
 * contrato: a situacao, a vigencia, o teto, o documento gerado pelo motor de
 * documentos, o "Assinar CCG" pelo provedor de assinatura e a versao assinada, que
 * volta ao lado da original. Ver docs/ADR/0063-ccg-do-tomador.md.
 *
 * O teto e regra sem fonte: aparece e e gravado, e nada decide cobertura de operacao
 * por ele. A CCG da operacao continua no painel da operacao, como antes.
 */

const situacoes: Record<
  string,
  { rotulo: string; tom: 'warning' | 'info' | 'success' | 'neutral' }
> = {
  rascunho: { rotulo: 'Rascunho', tom: 'warning' },
  em_assinatura: { rotulo: 'Em assinatura', tom: 'info' },
  assinado: { rotulo: 'Assinado', tom: 'success' },
  cancelado: { rotulo: 'Cancelado', tom: 'neutral' },
};

const situacoesDaParte: Record<
  string,
  { rotulo: string; tom: 'warning' | 'success' | 'danger' }
> = {
  pendente: { rotulo: 'Pendente', tom: 'warning' },
  assinado: { rotulo: 'Assinado', tom: 'success' },
  recusado: { rotulo: 'Recusado', tom: 'danger' },
};

const situacoesDoPedido: Record<string, string> = {
  pendente: 'Em andamento',
  assinado: 'Assinado',
  recusado: 'Recusado',
  expirado: 'Expirado',
  cancelado: 'Cancelado',
  erro: 'Erro no envio',
};

/**
 * O pedido que terminou sem assinatura, o unico que deixa o contrato voltar para
 * rascunho. O pedido assinado por todos, com a versao assinada ainda a caminho, nao
 * entra: a volta destravaria as partes de uma CCG ja assinada (achado R5-A06).
 */
const terminouSemAssinatura = new Set(['recusado', 'expirado', 'cancelado', 'erro']);

const historico: Record<string, string> = {
  criado: 'Contrato montado',
  alterado: 'Contrato alterado',
  documento_gerado: 'Documento gerado',
  assinatura_pedida: 'Assinatura pedida',
  assinado: 'Assinado',
  voltou_para_rascunho: 'Voltou para rascunho',
  cancelado: 'Cancelado',
};

const AJUDA_TETO =
  'Teto informado no contrato. Regra sem fonte: o valor e registrado e nao decide se uma operacao esta coberta pela CCG do tomador. A decisao fica com uma pessoa.';
const AJUDA_VIGENCIA =
  'Vigencia informada no contrato. E registro: nada vence nem deixa de valer sozinho pela data.';

interface Formulario {
  inicio: string;
  fim: string;
  teto: string;
  notas: string;
}

function formularioDe(detalhe: PolicyholderCcgDetail | undefined): Formulario {
  const contrato = detalhe?.contract;
  return {
    inicio: contrato?.valid_from ?? '',
    fim: contrato?.valid_until ?? '',
    teto: moneyToInput(contrato?.ceiling ?? null),
    notas: contrato?.notes ?? '',
  };
}

function entradaDe(formulario: Formulario): PolicyholderCcgInput {
  return {
    valid_from: formulario.inicio || null,
    valid_until: formulario.fim || null,
    ceiling: formulario.teto.trim() === '' ? null : toMoney(formulario.teto),
    notes: formulario.notas,
  };
}

function rotuloDoModelo(versao: PolicyholderCcgVersion): string {
  if (versao.kind === 'assinada') return 'Assinada no provedor';
  if (versao.template_source === 'sistema')
    return 'Modelo neutro do sistema (sem clausulado)';
  return versao.template_version
    ? `${versao.template_name} (versao ${versao.template_version})`
    : versao.template_name;
}

export function ContratoDaCcg({ tomador }: { tomador: Policyholder }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const chave = ['policyholder', tomador.id, 'ccg-contract'];
  const [formulario, setFormulario] = useState<Formulario | null>(null);
  const [assinar, setAssinar] = useState<{
    seguradora: boolean;
    mensagem: string;
    prazo: string;
  } | null>(null);
  const [motivo, setMotivo] = useState<string | null>(null);

  const consulta = useQuery({
    queryKey: chave,
    queryFn: ({ signal }) => policyholderCcg.get(tomador.id, signal),
  });
  const detalhe = consulta.data;
  const contrato = detalhe?.contract ?? null;

  function atualizar(novo: PolicyholderCcgDetail) {
    queryClient.setQueryData(chave, novo);
    void queryClient.invalidateQueries({ queryKey: ['policyholder', tomador.id] });
  }

  const salvar = useMutation({
    mutationFn: (dados: Formulario) =>
      contrato
        ? policyholderCcg.update(tomador.id, entradaDe(dados))
        : policyholderCcg.create(tomador.id, entradaDe(dados)),
    onSuccess: (novo) => {
      toast.success(contrato ? 'Contrato alterado.' : 'Contrato de CCG montado.');
      setFormulario(null);
      atualizar(novo);
    },
  });
  const gerar = useMutation({
    mutationFn: () => policyholderCcg.generate(tomador.id),
    onSuccess: (novo) => {
      toast.success('Documento gerado.');
      atualizar(novo);
    },
    onError: (erro: Error) => toast.error(erro.message),
  });
  const baixar = useMutation({
    mutationFn: (pedido: {
      versao?: number;
      formato?: 'pdf' | 'docx';
      contrato?: string;
    }) =>
      policyholderCcg.download(
        tomador.id,
        pedido.versao,
        pedido.formato,
        pedido.contrato,
      ),
    onSuccess: (arquivo) => {
      window.open(enderecoDeArquivo(arquivo.url), '_blank', 'noopener,noreferrer');
    },
    onError: (erro: Error) => toast.error(erro.message),
  });
  const pedirAssinatura = useMutation({
    mutationFn: () => {
      if (!assinar) throw new Error('Nenhum pedido em edicao.');
      return policyholderCcg.sign(tomador.id, {
        include_insurer_signer: assinar.seguradora,
        message: assinar.mensagem,
        deadline_days: assinar.prazo.trim() === '' ? 0 : Number(assinar.prazo),
      });
    },
    onSuccess: (novo) => {
      toast.success('Assinatura pedida ao provedor.');
      setAssinar(null);
      atualizar(novo);
    },
  });
  const reabrir = useMutation({
    mutationFn: () => policyholderCcg.reopen(tomador.id),
    onSuccess: (novo) => {
      toast.success('Contrato de volta ao rascunho.');
      atualizar(novo);
    },
    onError: (erro: Error) => toast.error(erro.message),
  });
  const cancelar = useMutation({
    mutationFn: () => policyholderCcg.cancel(tomador.id, motivo ?? ''),
    onSuccess: (novo) => {
      toast.success('Contrato cancelado.');
      setMotivo(null);
      atualizar(novo);
    },
  });

  if (consulta.isPending) {
    return <p className="text-sm text-ink-400">Carregando o contrato de CCG...</p>;
  }
  if (consulta.error) {
    return (
      <Alert tone="danger" title="Nao foi possivel ler o contrato de CCG">
        {consulta.error.message}
      </Alert>
    );
  }
  if (!detalhe) return null;

  const erroDoFormulario = salvar.error instanceof ApiError ? salvar.error : null;
  const tetoInvalido = formulario ? moneyInputError(formulario.teto) : undefined;
  const pedido = detalhe.signature_request;
  const rascunho = contrato?.status === 'rascunho';
  const situacao = contrato ? situacoes[contrato.status] : undefined;

  return (
    <Bloco
      titulo="Contrato de CCG do tomador"
      acao={
        contrato && situacao ? (
          <Badge tone={situacao.tom}>
            {`CCG ${contrato.number}: ${situacao.rotulo}`}
          </Badge>
        ) : null
      }
    >
      {!contrato ? (
        <div className="flex flex-col gap-2">
          <p className="text-sm text-ink-600">
            O tomador ainda nao tem contrato de CCG. A CCG da operacao continua no
            painel de cada operacao, como antes.
          </p>
          {detalhe.can_update ? (
            <div>
              <Button size="sm" onClick={() => setFormulario(formularioDe(detalhe))}>
                Montar contrato de CCG
              </Button>
            </div>
          ) : null}
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <Alert
            tone={
              contrato.notice_kind === 'sucesso'
                ? 'success'
                : contrato.notice_kind === 'erro'
                  ? 'danger'
                  : 'warning'
            }
            title={situacao?.rotulo ?? contrato.status}
          >
            {contrato.status_description}
          </Alert>
          <InfoGrid>
            <Info
              label="Inicio da vigencia"
              value={formatCivilDate(contrato.valid_from)}
              hint={AJUDA_VIGENCIA}
            />
            <Info
              label="Fim da vigencia"
              value={formatCivilDate(contrato.valid_until)}
              hint={AJUDA_VIGENCIA}
            />
            <Info
              label="Teto informado"
              value={formatMoney(contrato.ceiling)}
              hint={AJUDA_TETO}
            />
            <Info label="Observacao" value={contrato.notes} />
            <Info label="Montado por" value={contrato.created_label} />
            <Info label="Assinado em" value={formatDateTime(contrato.signed_at)} />
          </InfoGrid>
          <Alert tone="info" title="Cobertura de operacao pelo teto">
            {contrato.coverage.reason}
          </Alert>

          <div className="flex flex-wrap items-start gap-2">
            {rascunho && detalhe.can_update ? (
              <>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => setFormulario(formularioDe(detalhe))}
                >
                  Alterar contrato
                </Button>
                <Button
                  size="sm"
                  loading={gerar.isPending}
                  onClick={() => gerar.mutate()}
                >
                  Gerar documento
                </Button>
              </>
            ) : null}
            {detalhe.versions.length > 0 ? (
              <Button
                size="sm"
                variant="secondary"
                loading={baixar.isPending}
                onClick={() => baixar.mutate({})}
              >
                Baixar documento para conferencia
              </Button>
            ) : (
              <AcaoIndisponivel
                rotulo="Baixar documento para conferencia"
                motivo="Gere o documento do contrato primeiro."
              />
            )}
            {detalhe.can_sign ? (
              <Button
                size="sm"
                onClick={() =>
                  // A assinatura da seguradora comeca desmarcada: a dispensa (CCG-004)
                  // segue pendente, e quem pede escolhe. Marcada por padrao, com a
                  // configuracao em automatica, toda CCG pedida sem atencao sairia com a
                  // seguradora assinando sozinha (achado R5-B11).
                  setAssinar({ seguradora: false, mensagem: '', prazo: '' })
                }
              >
                Assinar CCG
              </Button>
            ) : (
              <AcaoIndisponivel rotulo="Assinar CCG" motivo={detalhe.can_sign_reason} />
            )}
            {contrato.status === 'em_assinatura' &&
            pedido &&
            terminouSemAssinatura.has(pedido.status) &&
            detalhe.can_update ? (
              <Button
                size="sm"
                variant="secondary"
                loading={reabrir.isPending}
                onClick={() => reabrir.mutate()}
              >
                Voltar para rascunho
              </Button>
            ) : null}
            {detalhe.can_cancel ? (
              <Button size="sm" variant="danger" onClick={() => setMotivo('')}>
                Cancelar contrato
              </Button>
            ) : null}
          </div>

          {pedido ? (
            <Alert
              tone={
                pedido.status === 'assinado'
                  ? 'success'
                  : pedido.open
                    ? 'info'
                    : 'warning'
              }
              title={`Pedido de assinatura ${pedido.number}: ${situacoesDoPedido[pedido.status] ?? pedido.status}`}
            >
              {pedido.provider_name || pedido.provider}
              {pedido.open ? `, prazo ate ${formatDateTime(pedido.deadline_at)}` : ''}
              {pedido.failure_message ? `. ${pedido.failure_message}` : ''}
              {pedido.status === 'assinado' && contrato.status === 'em_assinatura'
                ? '. Todos assinaram; a versao assinada ainda nao chegou do provedor. O contrato passa a assinado quando ela chegar.'
                : ''}
              {pedido.signers.some((s) => s.role === 'seguradora')
                ? '. A seguradora assina pelo signatario padrao da configuracao de assinatura.'
                : ''}
            </Alert>
          ) : null}

          {detalhe.parties.length > 0 ? (
            <DataTable
              columns={[
                {
                  key: 'ordem',
                  header: 'Ordem',
                  render: (p: PolicyholderCcgParty) => p.position,
                },
                {
                  key: 'papel',
                  header: 'Papel',
                  render: (p: PolicyholderCcgParty) => papeisDaCcg[p.role] ?? p.role,
                },
                {
                  key: 'nome',
                  header: 'Quem assina',
                  render: (p: PolicyholderCcgParty) =>
                    p.on_behalf_of
                      ? `${p.name} (pelo fiador ${p.on_behalf_of})`
                      : p.name,
                },
                {
                  key: 'cpf',
                  header: 'CPF',
                  render: (p: PolicyholderCcgParty) => p.masked_document || '-',
                },
                {
                  key: 'situacao',
                  header: 'Situacao da parte',
                  render: (p: PolicyholderCcgParty) => {
                    const s = situacoesDaParte[p.signature_status];
                    return s ? (
                      <Badge tone={s.tom}>{s.rotulo}</Badge>
                    ) : (
                      p.signature_status
                    );
                  },
                },
              ]}
              items={detalhe.parties}
              keyOf={(p) => `${p.position}-${p.party_id ?? p.role}`}
              empty={<p className="text-sm text-ink-400">Nenhuma parte no pedido.</p>}
            />
          ) : null}

          <DataTable
            columns={[
              {
                key: 'versao',
                header: 'Versao',
                render: (v: PolicyholderCcgVersion) => v.version,
              },
              {
                key: 'tipo',
                header: 'Tipo',
                render: (v: PolicyholderCcgVersion) =>
                  v.kind === 'assinada' ? (
                    <Badge tone="success">Assinada</Badge>
                  ) : (
                    <Badge tone="neutral">Gerada</Badge>
                  ),
              },
              { key: 'modelo', header: 'Modelo', render: rotuloDoModelo },
              {
                key: 'partes',
                header: 'Partes',
                render: (v: PolicyholderCcgVersion) =>
                  v.kind === 'gerada' ? v.parties_count : '-',
              },
              {
                key: 'em',
                header: 'Em',
                render: (v: PolicyholderCcgVersion) => formatDateTime(v.created_at),
              },
              {
                key: 'acoes',
                header: 'Acoes',
                render: (v: PolicyholderCcgVersion) => (
                  <div className="flex gap-2">
                    {v.has_pdf ? (
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() =>
                          baixar.mutate({ versao: v.version, formato: 'pdf' })
                        }
                      >
                        PDF
                      </Button>
                    ) : null}
                    {v.kind === 'gerada' ? (
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() =>
                          baixar.mutate({ versao: v.version, formato: 'docx' })
                        }
                      >
                        Word
                      </Button>
                    ) : null}
                  </div>
                ),
              },
            ]}
            items={detalhe.versions}
            keyOf={(v) => String(v.version)}
            empty={<p className="text-sm text-ink-400">Nenhum documento gerado.</p>}
          />

          <div className="flex flex-col gap-1">
            <h4 className="text-2xs font-semibold uppercase tracking-wide text-ink-500">
              Historico do contrato
            </h4>
            <ul className="flex flex-col gap-1 text-sm text-ink-700">
              {detalhe.events.map((evento) => (
                <li key={evento.id}>
                  <span className="text-ink-500">
                    {formatDateTime(evento.occurred_at)}
                  </span>{' '}
                  <span className="font-semibold">
                    {historico[evento.kind] ?? evento.kind}
                  </span>
                  {evento.actor_label ? ` por ${evento.actor_label}` : ''}:{' '}
                  {evento.message}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {detalhe.previous.length > 0 ? (
        <div className="flex flex-col gap-1">
          <h4 className="text-2xs font-semibold uppercase tracking-wide text-ink-500">
            Contratos anteriores
          </h4>
          <ul className="flex flex-col gap-1 text-xs text-ink-600">
            {detalhe.previous.map((anterior) => (
              <li key={anterior.id} className="flex flex-wrap items-center gap-2">
                <span>
                  {`CCG ${anterior.number} (${situacoes[anterior.status]?.rotulo ?? anterior.status}${
                    anterior.cancel_reason ? `: ${anterior.cancel_reason}` : ''
                  })`}
                </span>
                {anterior.versions.map((v) => (
                  <Button
                    key={v.version}
                    size="sm"
                    variant="secondary"
                    onClick={() =>
                      baixar.mutate({
                        contrato: anterior.id,
                        versao: v.version,
                        formato: v.has_pdf ? 'pdf' : 'docx',
                      })
                    }
                  >
                    {`Versao ${v.version} (${v.kind === 'assinada' ? 'assinada' : 'gerada'})`}
                  </Button>
                ))}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <Modal
        open={formulario !== null}
        onClose={() => setFormulario(null)}
        title={contrato ? 'Alterar contrato de CCG' : 'Montar contrato de CCG'}
        footer={
          <>
            <Button variant="secondary" onClick={() => setFormulario(null)}>
              Cancelar
            </Button>
            <Button
              loading={salvar.isPending}
              disabled={tetoInvalido !== undefined}
              onClick={() => formulario && salvar.mutate(formulario)}
            >
              Salvar
            </Button>
          </>
        }
      >
        {formulario ? (
          <div className="flex flex-col gap-4">
            {salvar.error ? (
              <Alert tone="danger" title="Nao foi possivel salvar">
                {salvar.error.message}
              </Alert>
            ) : null}
            <div className="grid gap-4 sm:grid-cols-2">
              <Field
                label="Inicio da vigencia"
                type="date"
                help={AJUDA_VIGENCIA}
                value={formulario.inicio}
                error={fieldErrorOf(erroDoFormulario, 'valid_from')}
                onChange={(e) =>
                  setFormulario({ ...formulario, inicio: e.target.value })
                }
              />
              <Field
                label="Fim da vigencia"
                type="date"
                help={AJUDA_VIGENCIA}
                value={formulario.fim}
                error={fieldErrorOf(erroDoFormulario, 'valid_until')}
                onChange={(e) => setFormulario({ ...formulario, fim: e.target.value })}
              />
              <MoneyField
                label="Teto informado (R$)"
                help={AJUDA_TETO}
                value={formulario.teto}
                error={tetoInvalido ?? fieldErrorOf(erroDoFormulario, 'ceiling')}
                onValueChange={(texto) => setFormulario({ ...formulario, teto: texto })}
              />
            </div>
            <Textarea
              label="Observacao do contrato"
              help="Texto livre de quem monta o contrato. Nao entra no documento."
              value={formulario.notas}
              maxLength={2000}
              onChange={(e) => setFormulario({ ...formulario, notas: e.target.value })}
            />
          </div>
        ) : null}
      </Modal>

      <Modal
        open={assinar !== null}
        onClose={() => setAssinar(null)}
        title="Assinar CCG"
        footer={
          <>
            <Button variant="secondary" onClick={() => setAssinar(null)}>
              Cancelar
            </Button>
            <Button
              loading={pedirAssinatura.isPending}
              onClick={() => pedirAssinatura.mutate()}
            >
              Pedir assinatura
            </Button>
          </>
        }
      >
        {assinar ? (
          <div className="flex flex-col gap-4">
            {pedirAssinatura.error ? (
              <Alert tone="danger" title="Nao foi possivel pedir a assinatura">
                {pedirAssinatura.error.message}
              </Alert>
            ) : null}
            <p className="text-sm text-ink-600">
              O pedido vai ao provedor de assinatura da empresa com os representantes
              legais, os fiadores pessoa fisica e o representante de cada fiador pessoa
              juridica, na ordem da secao. Cada um recebe o pedido pelo e-mail
              cadastrado na parte. Quem tem mais de um papel (o socio que e
              representante e fiador, por exemplo) assina uma vez so, pelos papeis
              juntos.
            </p>
            <label className="flex items-center gap-2 text-sm text-ink-700">
              <input
                type="checkbox"
                checked={assinar.seguradora}
                onChange={(e) =>
                  setAssinar({ ...assinar, seguradora: e.target.checked })
                }
              />
              Incluir a assinatura da seguradora (signatario padrao da configuracao de
              assinatura)
            </label>
            <Textarea
              label="Mensagem aos signatarios"
              help="Texto que o provedor envia junto com o pedido. Opcional."
              value={assinar.mensagem}
              maxLength={1000}
              onChange={(e) => setAssinar({ ...assinar, mensagem: e.target.value })}
            />
            <Field
              label="Prazo da assinatura (dias)"
              inputMode="numeric"
              help="De 1 a 90 dias corridos. Vazio usa o prazo da configuracao de assinatura."
              value={assinar.prazo}
              onChange={(e) => setAssinar({ ...assinar, prazo: e.target.value })}
            />
          </div>
        ) : null}
      </Modal>

      <Modal
        open={motivo !== null}
        onClose={() => setMotivo(null)}
        title="Cancelar contrato de CCG"
        footer={
          <>
            <Button variant="secondary" onClick={() => setMotivo(null)}>
              Voltar
            </Button>
            <Button
              variant="danger"
              loading={cancelar.isPending}
              disabled={!motivo || motivo.trim() === ''}
              onClick={() => cancelar.mutate()}
            >
              Cancelar contrato
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          {cancelar.error ? (
            <Alert tone="danger" title="Nao foi possivel cancelar">
              {cancelar.error.message}
            </Alert>
          ) : null}
          <p className="text-sm text-ink-600">
            O contrato sai de uso e fica no historico. Com pedido de assinatura em
            andamento, o pedido e cancelado no provedor. O efeito do cancelamento da CCG
            assinada sobre as apolices em vigor e regra sem fonte: nada mais muda.
          </p>
          <Textarea
            label="Motivo do cancelamento"
            help="Obrigatorio. E ele que explica, depois, por que o tomador ficou sem CCG."
            value={motivo ?? ''}
            maxLength={500}
            onChange={(e) => setMotivo(e.target.value)}
          />
        </div>
      </Modal>
    </Bloco>
  );
}
